// Contact API Service for backend integration
import { CONTACT_API_URL } from '../config/api';

const API_URL = CONTACT_API_URL;
const API_KEY = import.meta.env.VITE_AUTH_API_KEY;

export interface ContactFormData {
  firstName: string;
  lastName?: string;  // Made optional
  email: string;
  company: string;
  jobTitle?: string;  // Made optional
  subject: string;
  enquiryType: string;
  gpuTypes: string[];
  message: string;
  agreesToMarketing: boolean;
}

export interface ApiResponse {
  success: boolean;
  message: string;
  data?: any;
  errors?: any[];
}

class ContactApiService {
  private apiUrl: string;
  private apiKey: string;

  constructor() {
    this.apiUrl = API_URL;
    this.apiKey = API_KEY;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    // Add API key header if configured (for production)
    if (this.apiKey) {
      headers['X-API-Key'] = this.apiKey;
    }
    
    return headers;
  }

  async submitContactForm(formData: ContactFormData): Promise<ApiResponse> {
    try {
      const response = await fetch(`${this.apiUrl}/contact/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(API_KEY && { 'X-API-Key': API_KEY }),
        },
        body: JSON.stringify(formData),
      });

      let data;
      try {
        data = await response.json();
      } catch (e) {
        data = { success: false, message: 'Invalid response from server' };
      }

      // Check if response is not ok
      if (!response.ok) {
        // Handle different error status codes
        switch (response.status) {
          case 400:
            // Validation error
            if (data.errors) {
              const errorMessages = data.errors.map((e: any) => e.message).join(', ');
              throw new Error(`Validation failed: ${errorMessages}`);
            }
            throw new Error(data.message || 'Invalid request. Please check your input.');
            
          case 422:
            // Unprocessable Entity - validation error
            if (data.detail) {
              // FastAPI validation error format
              if (Array.isArray(data.detail)) {
                const errors = data.detail.map((e: any) => {
                  const field = e.loc?.join('.') || 'field';
                  return `${field}: ${e.msg}`;
                }).join(', ');
                throw new Error(`Validation failed: ${errors}`);
              }
              throw new Error(data.detail);
            }
            throw new Error(data.message || 'Invalid data. Please check all required fields.');
            
          case 429:
            // Rate limited
            throw new Error('Too many requests. Please try again in a few minutes.');
            
          case 503:
            // Service unavailable
            throw new Error('Email service temporarily unavailable. Please try again later or contact us directly.');
            
          default:
            // Generic server error
            throw new Error(data.message || `Server error: ${response.status}`);
        }
      }

      return data;
    } catch (error: any) {
      // Network or connection errors
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to server. Please check your internet connection or try again later.');
      }
      
      // Re-throw the error with proper message
      throw error;
    }
  }

  async testConnection(): Promise<ApiResponse> {
    try {
      const response = await fetch(`${this.apiUrl}/contact/test`, {
        headers: this.getHeaders(),
      });
      if (!response.ok) {
        throw new Error('API test failed');
      }
      return await response.json();
    } catch (error) {
      throw new Error('Cannot connect to email service');
    }
  }

  async checkHealth(): Promise<ApiResponse> {
    try {
      const response = await fetch(`${this.apiUrl}/health`, {
        headers: this.getHeaders(),
      });
      if (!response.ok) {
        throw new Error('Health check failed');
      }
      return await response.json();
    } catch (error) {
      throw new Error('Backend service is unavailable');
    }
  }
}

export const contactApiService = new ContactApiService();